import { useQuery } from "@tanstack/react-query";
import httpClient from "../httpClient";
import type { PricePayload, PriceResponse } from "./usePriceMutation";

// The amount is deliberately not among them: it is the server's own record of
// the payment, read from the status response, so a link cannot rewrite the
// figure a receipt shows.
export type PriceIdentifiers = Omit<PricePayload, "amount">;

const PRICE_KEYS = [
  "corporate_subsidiary_id",
  "payment_method_id",
  "corporate_service_id",
] as const;

// The price payload is converted into an array of key-value pairs, and then 
// written into the URL search params as the user moves from confirmation
// to payment page
export const priceSearch = (payload: PriceIdentifiers): string =>
  new URLSearchParams(
    PRICE_KEYS.flatMap(key =>
      payload[key] === undefined || payload[key] === null
        ? []
        : [[key, String(payload[key])] as [string, string]]
    )
  ).toString();

// The URL params are then read from the URL in their key-value pair form
export const readPriceSearch = (
  params: URLSearchParams
): PriceIdentifiers | undefined => {
  const [subsidiary, method, service] = PRICE_KEYS.map(key =>
    params.get(key)?.trim()
  );

  if (!subsidiary || !method || !service) return undefined;

  return {
    corporate_subsidiary_id: subsidiary,
    payment_method_id: method,
    corporate_service_id: service,
  };
};

type UseQueryPriceArgs = {
  payload?: PricePayload;
  enabled?: boolean;
  authorisation: string;
};

// The receipt's amounts are asked of the server on every load rather than
// carried in memory, so a reload shows the same figures the payer confirmed.
export function useQueryPrice({
  payload,
  enabled = true,
  authorisation,
}: UseQueryPriceArgs) {
  return useQuery<PriceResponse>(
    ["price", payload],
    () =>
      httpClient("payment/price", { authorisation }).get<PriceResponse>({
        ...payload,
      }),
    { enabled: enabled && !!payload, retry: 1, refetchOnWindowFocus: false }
  );
}
